import React, { useContext, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  Input,
  Link,
  Text,
} from "@chakra-ui/react";
import { PRIMARY_COLOR } from "../../constants/colors";
import api from "../../services/api";
import { toast } from "react-toastify";
import { AuthContext } from "../../contexts/AuthContext";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import decodeToken from "../../utils/decodeToken";
import { isValidCpfCnpj, maskCpfCnpj, onlyDigits } from "../../utils/cpfCnpj";
import {
  resolveCatalogContext,
  buildCatalogPath,
  buildLoginPath,
} from "../../utils/catalogContext";

const maskPhone = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 10) {
    return digits.replace(/(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3").trim();
  }
  return digits.replace(/(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3").trim();
};

export default function Register() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    instagram: "",
    cpfCnpj: "",
    password: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const from = location.state?.from;
  const { setUser } = useContext(AuthContext);

  const { clientHash, sellerId } = resolveCatalogContext(params);

  const handleChange = (field) => (e) => {
    let value = e.target.value;
    if (field === "phone") value = maskPhone(value);
    if (field === "cpfCnpj") value = maskCpfCnpj(value);
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isValidCpfCnpj(form.cpfCnpj)) {
      toast.error("CPF/CNPJ inválido!");
      return;
    }

    setIsSubmitting(true);

    try {
      await api.post("/clients", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        instagram: form.instagram,
        cpf_cnpj: onlyDigits(form.cpfCnpj),
        password: form.password,
        client_hash: clientHash,
      });

      const loginRes = await api.post("/login/clients", {
        email: form.email,
        password: form.password,
      });

      const token = loginRes.data.token;
      setUser({ token, info: loginRes.data.client });
      localStorage.setItem("@aqvex/user", JSON.stringify(loginRes.data.client));
      localStorage.setItem("@aqvex/token", JSON.stringify(token));

      const payload = decodeToken(token);
      if (payload?.exp) {
        const daysValid = Math.round((payload.exp - Date.now() / 1000) / 86400);
        toast.success(
          `Cadastro realizado com sucesso! Este acesso é válido por ${daysValid} dia${daysValid !== 1 ? "s" : ""}.`
        );
      } else {
        toast.success("Cadastro realizado com sucesso!");
      }

      navigate(from || buildCatalogPath(clientHash, sellerId), { replace: true });
    } catch (err) {
      toast.error(
        err.response?.data?.error ?? "Erro ao realizar cadastro, tente novamente mais tarde!"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const fields = [
    { label: "Nome completo *", field: "name", placeholder: "Digite seu nome", type: "text", required: true },
    { label: "E-mail *", field: "email", placeholder: "Digite seu e-mail", type: "email", required: true },
    { label: "Telefone *", field: "phone", placeholder: "(00) 00000-0000", type: "text", required: true },
    { label: "Instagram", field: "instagram", placeholder: "@seuperfil", type: "text", required: false },
    { label: "CPF / CNPJ *", field: "cpfCnpj", placeholder: "Digite seu CPF ou CNPJ", type: "text", required: true },
    { label: "Senha *", field: "password", placeholder: "Digite sua senha", type: "password", required: true },
  ];

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg="gray.50"
      p={4}
      flexDirection="column"
      gap="40px"
    >
      <Image src="/logo-aqvex.png" cursor="pointer" width="200px" />
      <Box
        as="form"
        onSubmit={handleSubmit}
        bg="white"
        rounded="lg"
        boxShadow="lg"
        p={{ base: 6, md: 10 }}
        width={{ base: "100%", sm: "420px" }}
      >
        <Flex flexDirection="column" width="100%" gap="10px">
          <Heading size="md" textAlign="center" mb={2}>
            Criar conta
          </Heading>
          {fields.map(({ label, field, placeholder, type, required }) => (
            <Flex key={field} flexDirection="column" gap="6px">
              <Text fontWeight="600">{label}</Text>
              <Input
                type={type}
                placeholder={placeholder}
                padding="15px"
                value={form[field]}
                onChange={handleChange(field)}
                required={required}
              />
            </Flex>
          ))}
          <Button
            type="submit"
            isFullWidth
            loading={isSubmitting}
            width="100%"
            backgroundColor={PRIMARY_COLOR}
            color="white"
            mt={2}
          >
            Cadastrar
          </Button>
          <Text textAlign="center" fontSize="sm" mt={2}>
            Já tem cadastro?{" "}
            <Link
              color={PRIMARY_COLOR}
              fontWeight="600"
              cursor="pointer"
              onClick={() =>
                navigate(buildLoginPath(clientHash, sellerId), { state: { from } })
              }
            >
              Entrar
            </Link>
          </Text>
        </Flex>
      </Box>
    </Flex>
  );
}
